import type { AttemptSummary } from '../services/classService';

/**
 * Student system: shared helpers for grades on class-assignment attempts, used by the
 * class page's Review & Grade, the attempt review page, My Grades and the session page.
 */

/** The longest teacher comment the backend keeps (`REVIEW_TEXT_MAX_LENGTH`). */
export const REVIEW_COMMENT_MAX_LENGTH = 4000;

/** Scores are stored with two decimals (NUMERIC(6, 2)). */
const TWO_DECIMALS = /^\d+(\.\d{1,2})?$/;

export type AttemptState = 'released' | 'draft' | 'practising' | 'awaiting_feedback' | 'feedback_failed' | 'to_review';

/** "3 / 4", or a dash when there is no score yet. */
export const formatScore = (score: number | null | undefined, max: number): string =>
  score === null || score === undefined ? '—' : `${Number(score.toFixed(2))} / ${max}`;

/** Where an attempt stands, for its status badge. */
export function attemptState(a: AttemptSummary): AttemptState {
  if (a.released_at) return 'released';
  if (a.draft_score !== null && a.draft_score !== undefined) return 'draft';
  if (a.session_status === 'in_progress') return 'practising';
  if (a.feedback_status === 'failed') return 'feedback_failed';
  if (a.feedback_status && a.feedback_status !== 'completed') return 'awaiting_feedback';
  return 'to_review';
}

/** The teacher's score for a row: the released grade, or their draft. */
export const teacherScore = (a: AttemptSummary): number | null | undefined => a.final_score ?? a.draft_score;

/** The score field's arrow step: halves on small scales, whole points on 0-100 style ones. */
export const scoreStep = (max: number): number => (max > 10 ? 1 : 0.5);

/** Why a typed score cannot be saved, or null when it can. Mirrors the backend: 0 to
 *  the scale's top, at most two decimals (any value in between is accepted; the step
 *  only sets the field's arrows). */
export function scoreProblem(text: string, max: number): 'range' | 'decimals' | null {
  const value = text.trim();
  if (!value) return null;
  const number = Number(value);
  if (Number.isNaN(number) || number < 0 || number > max) return 'range';
  if (!TWO_DECIMALS.test(value)) return 'decimals';
  return null;
}
